import React, { useState, useRef, useEffect, useCallback } from "react";
import Input from "@amzn/meridian/input";
import Select, { SelectOption } from "@amzn/meridian/select";
import DatePicker from "@amzn/meridian/date-picker";
import Tooltip from "@amzn/meridian/tooltip";
import { EditableFieldData, CellState } from "../../store/bulk-edit-store";
import { formatProjectFields } from "../activeProjects/ActiveProjectsUtils";
import "./BulkEditCell.scss";

interface BulkEditCellProps {
  projectId: string;
  fieldName: string;
  fieldData: EditableFieldData;
  cellState: CellState;
  validationError: string | null;
  onValueChange: (projectId: string, fieldName: string, newValue: string) => void;
  onNavigate: (direction: "tab" | "enter") => void;
  isActive: boolean;
  onActivate: () => void;
  // Optional overrides for fixed identifier columns (Status, LMSC/SC-SC, Timeline) that must
  // keep their existing grey identifier styling/position while still being editable.
  baseClassName?: string;
  cellStyle?: React.CSSProperties;
}

export default function BulkEditCell({
  projectId,
  fieldName,
  fieldData,
  cellState,
  validationError,
  onValueChange,
  onNavigate,
  isActive,
  onActivate,
  baseClassName,
  cellStyle,
}: BulkEditCellProps) {
  const [editValue, setEditValue] = useState(fieldData.value);
  const [isEditing, setIsEditing] = useState(false);
  const [dropdownSearchQuery, setDropdownSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isActive && !isEditing) {
      setIsEditing(true);
      setEditValue(fieldData.value);
      setDropdownSearchQuery("");
    }
  }, [isActive]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Sync external value changes (e.g., formula recalculation)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(fieldData.value);
    }
  }, [fieldData.value]);

  const confirmValue = useCallback(() => {
    setIsEditing(false);
    if (editValue !== fieldData.value) {
      onValueChange(projectId, fieldName, editValue);
    }
  }, [editValue, fieldData.value, projectId, fieldName, onValueChange]);

  const discardValue = useCallback(() => {
    setIsEditing(false);
    setEditValue(fieldData.value);
  }, [fieldData.value]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Tab") {
      event.preventDefault();
      confirmValue();
      onNavigate("tab");
    } else if (event.key === "Enter") {
      event.preventDefault();
      confirmValue();
      onNavigate("enter");
    } else if (event.key === "Escape") {
      event.preventDefault();
      discardValue();
    }
  };

  const handleBlur = () => {
    confirmValue();
  };

  const handleClick = () => {
    if (!isEditing) {
      onActivate();
    }
  };

  const getCellClassName = () => {
    // Fixed identifier columns (Status, LMSC/SC-SC, Timeline) keep their existing grey
    // identifier styling/color at rest — only the editing outline is layered on top.
    if (baseClassName) {
      return isEditing ? `${baseClassName} bulk-edit-cell-editing` : baseClassName;
    }
    let className = "bulk-edit-cell bulk-edit-editable-cell";
    if (isEditing) {
      className += " bulk-edit-cell-editing";
    } else if (cellState === "formula-updated") {
      className += " bulk-edit-cell-formula";
    } else if (cellState === "invalid") {
      className += " bulk-edit-cell-invalid";
    } else if (cellState === "valid-modified") {
      className += " bulk-edit-cell-modified";
    }
    return className;
  };

  const getDisplayValue = () => {
    return formatProjectFields(fieldData.value, fieldName) || "";
  };

  const renderEditControl = () => {
    switch (fieldData.fieldType) {
      case "dropdown": {
        // Same pattern as project details' SingleSelectDropdown: merge the typed search
        // query into the option list so users can either pick an existing value or type
        // a brand new one and select it.
        const originalOptions = fieldData.options || [];
        const matchedOptions = Array.from(
          new Set([
            ...originalOptions.filter((option) => !dropdownSearchQuery || option.includes(dropdownSearchQuery)),
            dropdownSearchQuery,
          ])
        )
          .filter((option) => option !== "")
          .sort();

        return (
          <Select
            value={editValue}
            onChange={(value) => {
              setEditValue(value);
              // Auto-confirm on dropdown selection (existing option or typed value)
              setTimeout(() => {
                onValueChange(projectId, fieldName, value);
                setIsEditing(false);
              }, 0);
            }}
            searchQuery={dropdownSearchQuery}
            onSearch={setDropdownSearchQuery}
            size="small"
            width="100%"
            placeholder="Select or type..."
          >
            {matchedOptions.map((option) => (
              <SelectOption key={option} value={option} label={option} />
            ))}
          </Select>
        );
      }
      case "date":
        return (
          <DatePicker
            value={editValue}
            onChange={(value) => {
              setEditValue(value);
              onValueChange(projectId, fieldName, value);
              setIsEditing(false);
            }}
            locale="en-CA"
          />
        );
      default:
        return (
          <Input
            ref={inputRef}
            value={editValue}
            onChange={setEditValue}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            size="small"
            width="100%"
          />
        );
    }
  };

  return (
    <td className={getCellClassName()} style={cellStyle} onClick={handleClick}>
      {isEditing ? (
        renderEditControl()
      ) : cellState === "invalid" && validationError ? (
        <Tooltip position="bottom" title={validationError}>
          <span className="bulk-edit-cell-error-content">
            <span className="bulk-edit-cell-error-flag">⚠</span>
            {getDisplayValue()}
          </span>
        </Tooltip>
      ) : cellState === "valid-modified" || cellState === "formula-updated" ? (
        <span className="bulk-edit-cell-diff">
          <span className="bulk-edit-cell-old-value">{formatProjectFields(fieldData.originalValue, fieldName) || ""}</span>
          <span className="bulk-edit-cell-new-value">{getDisplayValue()}</span>
        </span>
      ) : (
        getDisplayValue()
      )}
    </td>
  );
}
